import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { AuthGate } from './auth/AuthGate';
import { ProgressionProvider } from './auth/ProgressionProvider';
import { SeasonProvider } from './auth/SeasonProvider';
import { AnalyticsProvider } from './analytics/AnalyticsProvider';
import { HomePage } from './pages/HomePage';
import { PlayPage } from './pages/PlayPage';
import { BuilderPage } from './pages/BuilderPage';
import { BrowsePage } from './pages/BrowsePage';
import { CalibrationPage } from './pages/CalibrationPage';
import { ProfilePage } from './pages/ProfilePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RenderDebugPage } from './pages/RenderDebugPage';
import { TutorialPage } from './pages/TutorialPage';
import { ModePickerPage } from './pages/ModePickerPage';
import { SongsHubPage } from './pages/SongsHubPage';
import { SongDetailPage } from './pages/SongDetailPage';
import { SeasonsPage } from './pages/SeasonsPage';
import { CirclesHomePage } from './pages/circles/CirclesHomePage';
import { CirclesPlayPage } from './pages/circles/CirclesPlayPage';
import { CirclesBrowsePage } from './pages/circles/CirclesBrowsePage';
import { CirclesTutorialPage } from './pages/circles/CirclesTutorialPage';
import { CirclesSliderTutorialPage } from './pages/circles/CirclesSliderTutorialPage';
import { PianoHomePage } from './pages/piano/PianoHomePage';
import { PianoPlayPage } from './pages/piano/PianoPlayPage';
import { PianoTutorialPage } from './pages/piano/PianoTutorialPage';
import { GuitarHomePage } from './pages/guitar/GuitarHomePage';
import { GuitarPlayPage } from './pages/guitar/GuitarPlayPage';
import { GuitarBuilderPage } from './pages/guitar/GuitarBuilderPage';
import { GuitarBrowsePage } from './pages/guitar/GuitarBrowsePage';
import { GuitarTutorialPage } from './pages/guitar/GuitarTutorialPage';
import { WavesHomePage } from './pages/waves/WavesHomePage';
import { WavesPlayPage } from './pages/waves/WavesPlayPage';
import { WavesTutorialPage } from './pages/waves/WavesTutorialPage';
import { JumpHomePage } from './pages/jump/JumpHomePage';
import { JumpPlayPage } from './pages/jump/JumpPlayPage';
import { JumpTutorialPage } from './pages/jump/JumpTutorialPage';
import { AnalyticsDashboardPage } from './pages/AnalyticsDashboardPage';
import { TopNav } from './components/TopNav';
import { RouteHead } from './components/RouteHead';

/**
 * Helper that resolves the dynamic `:slug` param at render time and
 * issues a `<Navigate replace>` to the equivalent mode-prefixed path.
 * Used for the legacy `/play/:slug` and `/build/:slug` redirects so any
 * external links / bookmarks land in the new `/pulse/...` layout.
 */
function RedirectWithSlug({ to }: { to: (slug: string) => string }) {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={to(slug ?? '')} replace />;
}

/**
 * /debug/render bypasses the chrome (no nav, no AuthProvider, full-bleed
 * canvas) so screenshot harnesses get exactly the canvas pixels and
 * nothing else. Routing here keeps the debug page out of the regular
 * app shell entirely.
 */
function AppShell() {
  const location = useLocation();
  if (location.pathname.startsWith('/debug/render')) {
    return (
      <Routes>
        <Route path="/debug/render" element={<RenderDebugPage />} />
      </Routes>
    );
  }
  return (
    <AuthProvider>
      <ProgressionProvider>
        <SeasonProvider>
        <AnalyticsProvider>
          <div className="app-shell">
            {/*
              Keeps document.title + description/canonical/robots correct for
              every non-play route during SPA navigation (the server head is
              only correct for the initial HTML load). Play pages set their
              own song meta — RouteHead no-ops on /${mode}/play/:slug.
            */}
            <RouteHead />
            <TopNav />
            <main className="app-main">
              {/*
                AuthGate withholds route rendering until Firebase auth has
                finished initialising. Pages can therefore assume their
                `useEffect` data fetches will go out with a valid bearer
                token instead of racing the auth handshake and falling
                into spurious 401 / offline-mode banners on direct visits.
              */}
              <AuthGate>
                <Routes>
                {/*
                  / is the mode picker. From there players land in
                  /pulse/* or /circles/* depending on which game they want.
                  Universal pages (/profile, /calibrate) sit outside the
                  mode prefix because they shouldn't change the player's
                  mode context — Calibrate's audio offset, for instance,
                  applies to both modes.
                */}
                <Route path="/" element={<ModePickerPage />} />

                {/*
                  Tier-3 NCS content hub. `/songs` lists the catalogue ("best
                  NCS songs for gaming"); `/songs/:slug` is a per-track page
                  with play CTAs into all six modes and a "songs like it" list.
                  Top-of-funnel referral surface that funnels into the game.
                */}
                <Route path="/songs" element={<SongsHubPage />} />
                <Route path="/songs/:slug" element={<SongDetailPage />} />

                {/* Pulse mode */}
                <Route path="/pulse" element={<HomePage />} />
                <Route path="/pulse/play/:slug" element={<PlayPage />} />
                <Route path="/pulse/play" element={<Navigate to="/pulse" replace />} />
                <Route path="/pulse/browse" element={<BrowsePage />} />
                <Route path="/pulse/build" element={<BuilderPage />} />
                <Route path="/pulse/build/:slug" element={<BuilderPage />} />
                <Route path="/pulse/tutorial" element={<TutorialPage />} />

                {/*
                  Circles mode. Same route shape as Pulse so the picker,
                  TopNav and any external links stay symmetric.

                  Note: no /circles/build route. Circles ships without a
                  UGC editor — we curate a fixed osu-style catalogue.
                */}
                <Route path="/circles" element={<CirclesHomePage />} />
                <Route path="/circles/play/:slug" element={<CirclesPlayPage />} />
                <Route path="/circles/play" element={<Navigate to="/circles" replace />} />
                <Route path="/circles/browse" element={<CirclesBrowsePage />} />
                <Route path="/circles/tutorial" element={<CirclesTutorialPage />} />
                {/*
                  Slider onboarding. Content-gated (not first-visit
                  gated): CirclesPlayPage diverts here the first time a
                  player is about to enter a slider-bearing chart
                  (hard / extreme / insane). The `?next=<slug>` param
                  carries the chart they were heading for.
                */}
                <Route
                  path="/circles/tutorial/slider"
                  element={<CirclesSliderTutorialPage />}
                />

                {/*
                  Piano mode. 4-lane falling-tile waterfall. No
                  /piano/build route — charts are MIDI / onset-derived
                  with no UGC editor. /piano/tutorial teaches the
                  three tile shapes (tap, hold, dual) on the slowest
                  track in the catalogue; PianoHomePage gates a
                  first-time visit through it.
                */}
                <Route path="/piano" element={<PianoHomePage />} />
                <Route path="/piano/play/:slug" element={<PianoPlayPage />} />
                <Route path="/piano/play" element={<Navigate to="/piano" replace />} />
                <Route path="/piano/tutorial" element={<PianoTutorialPage />} />

                {/*
                  Guitar mode. 4-lane fretboard sourced from real
                  osu!mania community charts; shares the mania engine
                  with Piano but tunes for tighter timing windows +
                  faster approach + chords/jacks (no piano-tiles
                  min-spacing floor). Unlike Piano, Guitar ships a UGC
                  creator (`/guitar/build`) — a flat piano-roll editor
                  over the built-in songs — and a `/guitar/browse`
                  community discovery page. `/guitar/tutorial` gates a
                  first-time visit.
                */}
                <Route path="/guitar" element={<GuitarHomePage />} />
                <Route path="/guitar/play/:slug" element={<GuitarPlayPage />} />
                <Route path="/guitar/play" element={<Navigate to="/guitar" replace />} />
                <Route path="/guitar/browse" element={<GuitarBrowsePage />} />
                <Route path="/guitar/build" element={<GuitarBuilderPage />} />
                <Route path="/guitar/build/:slug" element={<GuitarBuilderPage />} />
                <Route path="/guitar/tutorial" element={<GuitarTutorialPage />} />

                {/*
                  Waves mode. Side-scrolling one-button corridor flight
                  derived from the same osu maps Circles/Guitar use. No
                  `/waves/build` (no UGC); `/waves/tutorial` gates the
                  first-time visit through a hand-crafted teaching chart.
                */}
                <Route path="/waves" element={<WavesHomePage />} />
                <Route path="/waves/play/:slug" element={<WavesPlayPage />} />
                <Route path="/waves/play" element={<Navigate to="/waves" replace />} />
                <Route path="/waves/tutorial" element={<WavesTutorialPage />} />

                {/*
                  Jump mode. One-button Geometry-Dash-cube runner derived
                  from the same osu maps Circles/Waves use. No `/jump/build`
                  (no UGC); `/jump/tutorial` gates the first-time visit
                  through a hand-crafted teaching chart. This mode owns the
                  `/jump/*` namespace — see the legacy-redirect note below
                  for why those old redirects were retired.
                */}
                <Route path="/jump" element={<JumpHomePage />} />
                <Route path="/jump/play/:slug" element={<JumpPlayPage />} />
                <Route path="/jump/play" element={<Navigate to="/jump" replace />} />
                <Route path="/jump/tutorial" element={<JumpTutorialPage />} />

                {/* Universal pages (not mode-prefixed) */}
                <Route path="/calibrate" element={<CalibrationPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                {/*
                  Seasons: the cross-mode competitive layer. Overall + per-mode
                  leaderboards ranked by total currency (stars + cups + crowns),
                  plus placement awards. Universal (not mode-prefixed) — it spans
                  every mode at once.
                */}
                <Route path="/seasons" element={<SeasonsPage />} />

                {/*
                  Admin-gated analytics dashboard. Server-side
                  {@link analyticsRouter} returns 403 for non-admin
                  uids, so a curious user landing here sees an error
                  state rather than an unauthorised data view.
                */}
                <Route path="/admin/analytics" element={<AnalyticsDashboardPage />} />

                {/*
                  Legacy redirects. The bare-path layout (/play/:slug,
                  /browse, /build, /tutorial) predates the multi-mode split
                  entirely; these keep every old bookmark, share link and
                  embedded doc link working by 301-equivalent SPA navigates
                  into /pulse/*. A `replace` so back-button doesn't bounce
                  between the old and new path forever.

                  Note: /pulse/* was briefly called "jump" pre-launch and
                  lived at /jump/*. That namespace now belongs to the new
                  Jump (cube-runner) mode above, so the old /jump → /pulse
                  redirects were deliberately retired. The track slugs are
                  shared across modes, so a stale /jump/play/<slug> bookmark
                  simply lands on the Jump version of that same song.
                */}
                <Route
                  path="/play/:slug"
                  element={<RedirectWithSlug to={(slug) => `/pulse/play/${slug}`} />}
                />
                <Route path="/play" element={<Navigate to="/pulse" replace />} />
                <Route path="/browse" element={<Navigate to="/pulse/browse" replace />} />
                <Route path="/build" element={<Navigate to="/pulse/build" replace />} />
                <Route
                  path="/build/:slug"
                  element={<RedirectWithSlug to={(slug) => `/pulse/build/${slug}`} />}
                />
                <Route path="/tutorial" element={<Navigate to="/pulse/tutorial" replace />} />

                <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </AuthGate>
            </main>
          </div>
        </AnalyticsProvider>
        </SeasonProvider>
      </ProgressionProvider>
    </AuthProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
